import React, { useState, useEffect } from 'react';
import { Upload, X, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { validateImage, convertToWebP } from '../../utils/imageUtils';
import { GalleryImage } from '../../types/gallery';
import { Tour } from '../../types/tours';

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: GalleryImage | null;
}

const GalleryModal: React.FC<GalleryModalProps> = ({ isOpen, onClose, image }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedTourId, setSelectedTourId] = useState<string>('');
  const [tours, setTours] = useState<Tour[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTours();
  }, []);

  useEffect(() => {
    if (image) {
      setTitle(image.title);
      setDescription(image.description);
      setImageUrl(image.image_url);
      setSelectedTourId(image.tour_id || '');
    } else {
      setTitle('');
      setDescription('');
      setImageUrl('');
      setSelectedTourId('');
    }
  }, [image]);

  const fetchTours = async () => {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('id, name, slug')
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setTours(data || []);
    } catch (err) {
      console.error('Error fetching tours:', err);
    }
  };

  const uploadImage = async (file: File) => {
    try {
      setUploading(true);
      setError(null);

      const validationError = validateImage(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      let convertedFile: File;
      try {
        convertedFile = await convertToWebP(file);
      } catch (convErr) {
        setError(convErr instanceof Error ? convErr.message : 'Failed to process image. Try a different file.');
        return;
      }

      const ext = convertedFile.type === 'image/webp' ? 'webp' : 'jpg';
      const fileName = `${Math.random()}.${ext}`;
      const filePath = `gallery/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('gallery')
        .upload(filePath, convertedFile, {
          contentType: convertedFile.type,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('gallery')
        .getPublicUrl(filePath);

      setImageUrl(data.publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      const message = error instanceof Error ? error.message : 'Failed to upload image';
      setError(`Upload failed: ${message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!imageUrl) {
      setError('Please upload an image');
      return;
    }

    try {
      const data = {
        title,
        description,
        image_url: imageUrl,
        tour_id: selectedTourId || null
      };

      if (image) {
        const { error } = await supabase
          .from('gallery_images')
          .update(data)
          .eq('id', image.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('gallery_images')
          .insert([data]);
        if (error) throw error;
      }

      onClose();
    } catch (err) {
      console.error('Error saving image:', err);
      setError('Failed to save image');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#1F2B1F] rounded-xl w-full max-w-2xl">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold">
            {image ? 'Edit Image' : 'Add New Image'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Image
            </label>
            {imageUrl ? (
              <div className="relative aspect-video">
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setImageUrl('')}
                  className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <label className="relative aspect-video cursor-pointer block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadImage(file);
                  }}
                  disabled={uploading}
                  className="hidden"
                />
                <div className="absolute inset-0 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center hover:border-white/40 transition-colors">
                  <div className="text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-white/40" />
                    <span className="text-sm text-white/40">
                      {uploading ? 'Uploading...' : 'Click to upload'}
                    </span>
                  </div>
                </div>
              </label>
            )}
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-white/70 mb-2">
              Title
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-white/70 mb-2">
              Description (max 100 characters)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={100}
              rows={2}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white resize-none"
              required
            />
            <p className="text-sm text-white/50 mt-1">
              {description.length}/100 characters
            </p>
          </div>

          {/* Tour Association */}
          <div>
            <label htmlFor="tour" className="block text-sm font-medium text-white/70 mb-2">
              Associated Tour (optional)
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <select
                id="tour"
                value={selectedTourId}
                onChange={(e) => setSelectedTourId(e.target.value)}
                className="w-full pl-12 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white appearance-none"
              >
                <option value="" className="bg-[#1F2B1F]">No tour association</option>
                {tours.map((tour) => (
                  <option key={tour.id} value={tour.id} className="bg-[#1F2B1F]">
                    {tour.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-6 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : image ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GalleryModal;